import i18next from 'i18next';

import { getProjectOrgIds } from '@/features/charts/api/get-project-org-ids';
import {
  AggregationIntervall,
  AggregationType,
  ChartType,
} from '@/features/charts/components/create-chart-type-selection';
import { KpiAnomaly } from '@/features/insights/api/detect-kpi-anomalies';
import { PartnerPopulated } from '@/features/partners/api/get-partners';
import { KpiForecast, KpiType, KpiWithEntries } from '@/shared/types/api';
import { getYearMonthOnly } from '@/shared/utils/date-utils';

export type CustomToolTipData = {
  payload: [
    {
      payload: {
        dataKey: 'data';
        value: number;
        name: string;
        budget?: number | null;
        unit?: string | null;
        date?: string | null;
        lastEntryValue?: number | null;
        growthRate?: number | null;
      };
      value: number;
    },
  ];
};

export const getKpiValueDataKey = (kpiId: number) => {
  return `value-${kpiId}`;
};

type ChartDataItem = {
  name?: string;
  kpiName: string;
  kpiId: number;
  aggregationType: AggregationType;
  date?: number;
  unit?: string | null;
  budget?: number | null;
  lastEntryValue?: number | null;
  index?: number;
  isForecast?: boolean;
  isAnomaly?: boolean;
  deviationPercentage?: number | null;
  fill?: string;
  value: number | null;
  forecastValue?: number | null;
  forecastConfidence?: number | null;
  forecastUpperBound?: number | null;
  forecastLowerBound?: number | null;
  partnerName?: string | null;
};

export type ChartData = ChartDataItem[];

export type ChartConfig = Record<number, { label: string }>;

export type ChartResult = {
  chartConfig: ChartConfig;
  chartData: ChartData;
};

export type ChartSettings = {
  hideXAxis?: boolean;
  hideYAxis?: boolean;
  hideGrid?: boolean;
  height?: string;
  hidePlaceholderIfEmpty?: boolean;
};

export interface UniqueTicksResult {
  uniqueTicks: number[];
  tickLabels: Map<number, string>;
}

// Calendar-friendly step sizes for tick thinning (monthly intervals)
const NICE_TICK_STEPS = [1, 2, 3, 4, 6, 12];

function getNiceStep(labelCount: number, maxTicks: number): number {
  if (labelCount <= maxTicks) return 1;
  const rawStep = Math.ceil(labelCount / maxTicks);
  return NICE_TICK_STEPS.find((s) => s >= rawStep) ?? rawStep;
}

export function calculateUniqueTicksWithEqualSpacing(
  chartData: ChartData,
  maxTicks: number = 12,
): UniqueTicksResult {
  const uniqueLabelsInOrder: string[] = [];
  const seenLabels = new Set<string>();

  for (const d of chartData) {
    if (d.name && !seenLabels.has(d.name)) {
      seenLabels.add(d.name);
      uniqueLabelsInOrder.push(d.name);
    }
  }

  const dates = chartData.map((d) => d.date).filter(Boolean) as number[];
  if (dates.length === 0 || uniqueLabelsInOrder.length === 0) {
    return { uniqueTicks: [], tickLabels: new Map<number, string>() };
  }

  const minDate = Math.min(...dates);
  const maxDate = Math.max(...dates);
  const range = maxDate - minDate;
  const labelCount = uniqueLabelsInOrder.length;

  // Determine which label indices to show
  const step = getNiceStep(labelCount, maxTicks);

  const selectedIndices: number[] = [];
  for (let i = 0; i < labelCount; i += step) {
    selectedIndices.push(i);
  }
  // Always include the last label for context
  if (selectedIndices[selectedIndices.length - 1] !== labelCount - 1) {
    selectedIndices.push(labelCount - 1);
  }

  const ticks: number[] = [];
  const labelsMap = new Map<number, string>();

  for (const i of selectedIndices) {
    const position =
      labelCount === 1 ? minDate : minDate + (range * i) / (labelCount - 1);
    ticks.push(position);
    labelsMap.set(position, uniqueLabelsInOrder[i]);
  }

  return { uniqueTicks: ticks, tickLabels: labelsMap };
}

export async function generateChartConfigAndData({
  kpis,
  aggregationType,
  aggregationIntervall,
  colors,
  chartType,
  forecastData,
  anomalyData,
  partners,
}: {
  kpis: KpiWithEntries[];
  aggregationType: AggregationType;
  aggregationIntervall?: AggregationIntervall | null;
  colors?: string[];
  chartType: ChartType;
  forecastData?: Array<KpiForecast>;
  anomalyData?: Array<KpiAnomaly>;
  partners: PartnerPopulated[];
}): Promise<ChartResult> {
  if (aggregationType === 'timeline' && !aggregationIntervall) {
    return { chartConfig: {}, chartData: [] };
  }

  // Collect all project_ids from entries for batch lookup
  const projectIds = new Set<number>();
  kpis.forEach((kpi) => {
    kpi.entries?.forEach((entry) => {
      if (entry.project_id) projectIds.add(entry.project_id);
    });
  });

  // Batch fetch org_ids for all projects
  const projectOrgMap = await getProjectOrgIds([...projectIds]);

  const generator = {
    timeline: () =>
      generateTimeline(
        kpis,
        aggregationIntervall!,
        partners,
        projectOrgMap,
        forecastData,
        anomalyData,
      ),
    accumulated: () =>
      generateAccumulated(kpis, chartType, partners, projectOrgMap, colors),
  }[aggregationType];

  return generator ? generator() : { chartConfig: {}, chartData: [] };
}

function generateAccumulated(
  kpis: KpiWithEntries[],
  chartType: ChartType,
  partners: PartnerPopulated[],
  projectOrgMap: Map<number, number>,
  colors?: string[],
) {
  const chartConfig: ChartConfig = {};
  const chartData: ChartData = [];

  kpis.forEach((kpi, index) => {
    const entries = kpi.entries || [];
    const lastEntry = entries[entries.length - 1];
    const lastEntryValue = lastEntry?.value ?? 0;
    const secondLastEntry =
      entries.length > 1 ? entries[entries.length - 2] : null;
    const secondLastEntryValue = secondLastEntry?.value ?? null;
    const lastEntryDate = lastEntry?.created_at
      ? new Date(lastEntry.created_at).getTime()
      : undefined;

    // Synchronous partner matching using pre-fetched org map
    const orgId = lastEntry?.project_id
      ? projectOrgMap.get(lastEntry.project_id)
      : undefined;
    const partner = orgId
      ? partners.find((p) => p.partner_orga_id === orgId)
      : undefined;

    const dataItem: ChartDataItem = {
      kpiId: kpi.id,
      kpiName: kpi.name,
      name: kpi.name,
      value: lastEntryValue,
      aggregationType: AggregationType.ACCUMULATED,
      unit: kpi.unit,
      budget: kpi.max_value,
      ...(secondLastEntryValue !== null && {
        lastEntryValue: secondLastEntryValue,
      }),
      ...(lastEntryDate && { date: lastEntryDate }),
      [getKpiValueDataKey(kpi.id)]: lastEntryValue,
      ...(partner && { partnerName: partner.alias }),
    };

    const isSingleKpiBar = chartType == ChartType.BAR && kpis.length === 1;

    if (colors && colors[index] && !isSingleKpiBar) {
      dataItem.fill = colors[index];
    }

    chartData.push(dataItem);

    chartConfig[kpi.id] = {
      label: kpi.name,
    };
  });

  return { chartConfig, chartData };
}

function generateTimeline(
  kpis: KpiWithEntries[],
  period: AggregationIntervall,
  partners: PartnerPopulated[],
  projectOrgMap: Map<number, number>,
  forecastData?: Array<KpiForecast>,
  anomalyData?: Array<KpiAnomaly>,
) {
  const chartConfig: ChartConfig = {};
  const chartData: ChartData = [];

  const kpiNames = new Set<string>();
  kpis.forEach((kpi) => {
    kpiNames.add(kpi.name);
  });

  kpis.forEach((kpi) => {
    chartConfig[kpi.id] = {
      label: kpi.name,
    };

    kpi.entries?.forEach((entry, index) => {
      if (entry.value == null || !entry.created_at) return;

      const date = new Date(entry.created_at);
      const monthYearDate = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthYearTimeKey = getTimeKey(monthYearDate, period);
      const lastEntryValue = index > 0 ? kpi.entries![index - 1].value : null;

      // Synchronous partner matching using pre-fetched org map
      const orgId = projectOrgMap.get(entry.project_id);
      const partner = orgId
        ? partners.find((p) => p.partner_orga_id === orgId)
        : undefined;

      chartData.push({
        kpiId: kpi.id,
        kpiName: kpi.name,
        name: monthYearTimeKey,
        aggregationType: AggregationType.TIMELINE,
        date: new Date(entry.created_at).getTime(),
        value: entry.value ?? 0,
        ...(kpi.unit && { unit: kpi.unit }),
        ...(kpi.max_value && {
          budget: kpi.max_value,
        }),
        ...(lastEntryValue && {
          lastEntryValue: lastEntryValue,
        }),
        ...(partner && { partnerName: partner.alias }),
      });
    });
  });

  if (anomalyData && anomalyData.length > 0) {
    chartData.forEach((entryData) => {
      const entryDate = new Date(entryData.date ?? '');
      const anomalyInMonth = anomalyData.find((anomaly) => {
        const anomalyDate = new Date(anomaly.period ?? '');
        return (
          getYearMonthOnly(anomalyDate).getTime() ===
          getYearMonthOnly(entryDate).getTime()
        );
      });
      if (anomalyInMonth && anomalyInMonth.is_anomaly) {
        entryData.isAnomaly = true;
        entryData.deviationPercentage = anomalyInMonth.deviation_percentage;
      }
    });
  }

  if (forecastData && forecastData.length > 0) {
    const kpi = kpis[0];

    // Find the last actual entry value for growth rate calculations
    const lastActualEntry =
      kpi.entries && kpi.entries.length > 0
        ? kpi.entries[kpi.entries.length - 1]
        : null;
    const lastActualValue = lastActualEntry?.value ?? null;

    // Determine date range for filtering forecasts
    const actualEntries = chartData.filter((d) => !d.isForecast);
    const lastActualDate =
      actualEntries.length > 0
        ? Math.max(...actualEntries.map((d) => d.date || 0))
        : Date.now();

    // Calculate how many forecast periods to show based on aggregation period
    const forecastPeriodsToShow =
      period === 'yearly' ? 3 : period === 'quarterly' ? 4 : 6;

    forecastData
      .filter((forecast) => {
        const forecastDate = parseForecastPeriodToDate(forecast.period);
        const forecastTimestamp = forecastDate.getTime();

        // Period duration in milliseconds
        const periodInMs =
          period === 'yearly'
            ? 365 * 24 * 60 * 60 * 1000
            : period === 'quarterly'
              ? 90 * 24 * 60 * 60 * 1000
              : 30 * 24 * 60 * 60 * 1000;

        const maxForecastDate =
          lastActualDate + forecastPeriodsToShow * periodInMs;

        // Only include forecasts that are after last actual and within visible window
        return (
          forecastTimestamp >= lastActualDate &&
          forecastTimestamp <= maxForecastDate
        );
      })
      .forEach((forecast) => {
        const timeKey = parseForecastPeriodToTimeKey(forecast.period, period);
        const forecastDate = parseForecastPeriodToDate(forecast.period);

        const forecastTimeline: ChartDataItem = {
          kpiId: kpi.id,
          kpiName: kpi.name,
          name: timeKey,
          aggregationType: AggregationType.TIMELINE,
          date: forecastDate.getTime(),
          value: null,
          forecastValue: forecast.forecast_value,
          forecastConfidence: forecast.confidence,
          forecastUpperBound: forecast.upper_bound,
          forecastLowerBound: forecast.lower_bound,
          ...(kpi?.unit && { unit: kpi.unit }),
          ...(kpi?.max_value && {
            budget: kpi.max_value,
          }),
          ...(lastActualValue !== null && {
            lastEntryValue: lastActualValue,
          }),
          isForecast: true,
          isAnomaly:
            forecast.forecast_value > forecast.upper_bound ||
            forecast.forecast_value < forecast.lower_bound,
        };

        chartData.push(forecastTimeline);
      });
  }

  const chartDataFormatted = chartData
    .sort((a, b) => {
      const dateObjA = a.date ? new Date(a.date) : new Date();
      const dateObjB = b.date ? new Date(b.date) : new Date();

      const dateCompare = dateObjA.getTime() - dateObjB.getTime();
      if (dateCompare !== 0) return dateCompare;
      return a.isForecast === b.isForecast ? 0 : a.isForecast ? 1 : -1;
    })
    .map((entryData, index) => {
      return {
        isForecast: entryData.isForecast || false,
        isAnomaly: entryData.isAnomaly || false,
        index,
        ...entryData,
        [getKpiValueDataKey(entryData.kpiId)]: entryData.value,
      };
    });

  return { chartConfig, chartData: chartDataFormatted };
}

function parseForecastPeriodToDate(forecastPeriod: string): Date {
  if (forecastPeriod.includes('-Q')) {
    const [year, quarter] = forecastPeriod.split('-Q');
    const quarterNum = parseInt(quarter);
    const month = (quarterNum - 1) * 3;
    return new Date(parseInt(year), month, 1);
  } else if (forecastPeriod.includes('-')) {
    const [year, month] = forecastPeriod.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, 1);
  } else {
    return new Date(parseInt(forecastPeriod), 0, 1);
  }
}

function parseForecastPeriodToTimeKey(
  forecastPeriod: string,
  aggregationPeriod: AggregationIntervall,
): string {
  const date = parseForecastPeriodToDate(forecastPeriod);
  return getTimeKey(date, aggregationPeriod);
}

export function getTimeKey(date: Date, period: AggregationIntervall): string {
  const locale = i18next.language || 'en';
  const formatters = {
    daily: (d: Date) => d.toISOString().split('T')[0],
    weekly: (d: Date) => `KW ${getWeekNumber(d)}, ${d.getFullYear()}`,
    monthly: (d: Date) =>
      d.toLocaleString(locale, { month: 'short', year: '2-digit' }),
    quarterly: (d: Date) => {
      const quarter = Math.floor(d.getMonth() / 3) + 1;
      return `Q${quarter} ${d.getFullYear()}`;
    },
    yearly: (d: Date) => d.getFullYear().toString(),
  };
  return formatters[period](date);
}

function getWeekNumber(date: Date): number {
  const firstJan = new Date(date.getFullYear(), 0, 1);
  const daysSinceFirstJan = Math.floor(
    (date.getTime() - firstJan.getTime()) / (24 * 60 * 60 * 1000),
  );
  return Math.ceil((daysSinceFirstJan + firstJan.getDay() + 1) / 7);
}

export function generateColorVariants(
  count: number,
  baseHsl = '274, 89, 45',
): string[] {
  const [hue, saturation, lightness] = baseHsl.split(',').map(Number);

  // If count is 1, return the base color without any shifts
  if (count === 1) {
    return [`hsl(${hue}, ${saturation}%, ${lightness}%)`];
  }

  return Array.from({ length: count }, (_, i) => {
    const hueShift = (i / count) * 10 - 5;
    const satShift = (i % 2 === 0 ? 1 : -1) * 5;
    const lightShift = (i / count) * 10 - 5;

    return `hsl(${(hue + hueShift) % 360}, ${Math.max(
      80,
      Math.min(95, saturation + satShift),
    )}%, ${Math.max(35, Math.min(55, lightness + lightShift))}%)`;
  });
}

type ChartStats = {
  max: number;
  min: number;
  avg: number;
  changeRate: number;
};

const calculateMin = (values: number[]): number => {
  return values.length > 0 ? Math.min(...values) : 0;
};

const calculateMax = (values: number[]): number => {
  return values.length > 0 ? Math.max(...values) : 0;
};

const calculateAvg = (values: number[]): number => {
  return values.length > 0
    ? values.reduce((sum, val) => sum + val, 0) / values.length
    : 0;
};

const calculateChangeRate = (values: number[]): number => {
  if (values.length < 2) return 0;

  const firstValue = values[0];
  const lastValue = values[values.length - 1];

  if (firstValue === 0) return 0;

  return ((lastValue - firstValue) / firstValue) * 100;
};

export const extractValues = (
  chartData: ChartData,
  aggregationType: AggregationType,
): number[] => {
  if (aggregationType === 'timeline') {
    return chartData
      .map((entry) => {
        // For forecast entries, use forecastValue instead of value
        if (
          entry.isForecast &&
          entry.forecastValue !== null &&
          entry.forecastValue !== undefined
        ) {
          return Number(entry.forecastValue);
        }
        return Number(entry.value);
      })
      .filter((num) => !isNaN(num) && num !== null);
  }
  return chartData
    .map((entry) => Number(entry.value))
    .filter((num) => !isNaN(num));
};

export const calculateChartStats = ({
  chartData,
  aggregationType,
}: {
  chartData: ChartData;
  aggregationType: AggregationType;
}): ChartStats => {
  const values = extractValues(chartData, aggregationType);

  return {
    min: calculateMin(values),
    max: calculateMax(values),
    avg: calculateAvg(values),
    changeRate: calculateChangeRate(values),
  };
};

/// Returns a list of available chart types dependent
// on the aggregation type, the kpi type and unit
export const getAvailableChartTypes = ({
  aggregationType,
  kpiType,
  kpiLength = 1,
  maxValue,
}: {
  aggregationType: AggregationType;
  kpiType: KpiType | null;
  kpiLength?: number;
  maxValue?: number | null;
}): ChartType[] => {
  switch (aggregationType) {
    case 'timeline':
      return [ChartType.LINE, ChartType.BAR];
    case 'accumulated':
      if (kpiType === 'relative') {
        return [ChartType.BATTERY, ChartType.PIE];
      } else if (maxValue) {
        return [ChartType.BAR, ChartType.BATTERY, ChartType.PIE];
      } else if (kpiLength > 1) {
        return [ChartType.BAR, ChartType.PIE];
      } else {
        return [ChartType.BAR];
      }
    default:
      return [];
  }
};

export async function generateMockChartData(
  chartType: ChartType,
  aggregationType: AggregationType,
  maxValue?: number,
): Promise<ChartResult> {
  const baseDate = new Date();

  const minValue = maxValue ? Math.floor(maxValue * 0.1) : 50;
  const range = maxValue ? Math.floor(maxValue * 0.8) : 200;

  const mockKpis = [
    {
      id: 1,
      name: 'Beispiel KPI',
      unit: 'EUR',
      kpi_tracking_entries: Array.from({ length: 7 }, (_, i) => ({
        id: i + 1,
        value: Math.floor(Math.random() * range) + minValue,
        created_at: new Date(
          baseDate.getTime() - (6 - i) * 24 * 60 * 60 * 1000,
        ).toISOString(),
      })),
    },
  ];

  return generateChartConfigAndData({
    kpis: mockKpis as any,
    aggregationType,
    aggregationIntervall: aggregationType === 'timeline' ? 'daily' : undefined,
    colors: ['#8884d8'],
    chartType,
    partners: [],
  });
}
